import { useSelector } from '~/_shared/utils/hooks/useSelector';
import { createStateSelector } from '~/_shared/utils/memoize/createSelector';
import {
  type BoundaryId, type BoundaryTerritoryGroupId, type BoundaryTerritoryId,
} from '~/store/boundaries/boundaryIdentifier.type';
import { type AppState } from '../app.store';
import { type BoundaryTerritoryGroupDetailsItem } from './boundaryTerritoryGroups.state';

export const boundaryTerritoryDetailsFilterHashSelector = (state: AppState) => state.map.boundaryTerritoryDetails.territoryDetails.filterHash;

export const boundaryTerritoryDetailsSelector = (state: AppState) => state.map.boundaryTerritoryDetails.territoryDetails.data;
export const useBoundaryTerritoryDetailsSelector = () => useSelector(boundaryTerritoryDetailsSelector);

export const boundaryTerritoryDetailsIsLoadingSelector = (state: AppState) => state.map.boundaryTerritoryDetails.isLoading;
export const useBoundaryTerritoryDetailsIsLoadingSelector = () => useSelector(boundaryTerritoryDetailsIsLoadingSelector);

export type BoundaryTerritoryAssignment = {
  boundaryTerritoryGroupId: BoundaryTerritoryGroupId;
  territoryAssignments: ReadonlyMap<BoundaryTerritoryId, ReadonlySet<BoundaryId>>;
  boundaryAssignments: ReadonlyMap<BoundaryId, BoundaryTerritoryId>;
};

export const boundaryTerritoryAssignmentsSelector = createStateSelector(
  boundaryTerritoryDetailsSelector,
  (details) => {
    const boundaryTerritoryGroupIds = Array.from(details.keys());

    return new Map(boundaryTerritoryGroupIds.map(BTGId => {
      const territoryAssignments: Map<BoundaryTerritoryId, Set<BoundaryId>> = new Map();
      const boundaryAssignments: Map<BoundaryId, BoundaryTerritoryId> = new Map();

      const itemDetails = details.get(BTGId);
      const generatedAssignments = itemDetails?.assignments ?? {};
      const manualAssignments = itemDetails?.manualAssignments ?? {};

      // Manual assignments
      for (const [territoryId, assignments] of Object.entries(manualAssignments)) {
        const territoryBoundaries = new Set<number>();

        for (const boundaryId of assignments) {
          territoryBoundaries.add(boundaryId);
          boundaryAssignments.set(boundaryId, territoryId);
        }

        territoryAssignments.set(territoryId, territoryBoundaries);
      }

      // Generated assignments
      for (const [territoryId, assignments] of Object.entries(generatedAssignments)) {
        const territoryBoundaries = territoryAssignments.get(territoryId) ?? new Set<number>();

        for (const boundaryId of assignments) {

          // Manual assignments take precedence.
          // If an assignment already exists, do not process the generated assignment.
          if (!boundaryAssignments.has(boundaryId)) {
            territoryBoundaries.add(boundaryId);
            boundaryAssignments.set(boundaryId, territoryId);
          }
        }

        territoryAssignments.set(territoryId, territoryBoundaries);
      }

      return [BTGId, {
        boundaryTerritoryGroupId: BTGId,
        territoryAssignments,
        boundaryAssignments,
        extraData: itemDetails?.extraData,
      }];
    }));
  });

export const useBoundaryTerritoryAssignments = () => useSelector(boundaryTerritoryAssignmentsSelector);

export type BoundaryTerritoryCountsAndExtraData = {
  boundaryTerritoryGroupId: BoundaryTerritoryGroupId;
  counts?: Readonly<{ [boundaryId: BoundaryId]: number }>;
  extraData?: BoundaryTerritoryGroupDetailsItem['extraData'];
};

export const boundaryTerritoryExtraDataSelector = createStateSelector(
  boundaryTerritoryDetailsSelector,
  (details) => {
    const boundaryTerritoryGroupIds = Array.from(details.keys());

    return new Map(boundaryTerritoryGroupIds.map(BTGId => {
      const itemDetails = details.get(BTGId);

      return [BTGId, {
        boundaryTerritoryGroupId: BTGId,
        counts: itemDetails?.counts,
        extraData: itemDetails?.extraData,
      }];
    }));
  });

export const useBoundaryTerritoryExtraData = () => useSelector(boundaryTerritoryExtraDataSelector);
